/**
 * Cloudflare Turnstile for the public forms (/remove, /waitlist): the site key from the API, an explicit render with the
 * form's action (the API checks it), and `blocked` when the script or widget can't load (e.g. an ad blocker), so the
 * page can point to email instead of a dead button. Render `<div ref={box} />` once `siteKey` is set.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../../api';

declare global {
  interface Window { turnstile?: { render: (el: HTMLElement, o: { sitekey: string; action?: string; callback: (t: string) => void; 'expired-callback'?: () => void; 'error-callback'?: () => void }) => string; reset: (id?: string) => void } }
}

export function useTurnstile(action: string) {
  const [siteKey, setSiteKey] = useState<string | null | undefined>(undefined);
  const [token, setToken] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);

  useEffect(() => { api<{ turnstileSiteKey: string | null }>('/api/public/removal-config').then((r) => setSiteKey(r.turnstileSiteKey), () => setSiteKey(null)); }, []);
  useEffect(() => {
    if (!siteKey || !box.current) return;
    const render = () => {
      if (window.turnstile && box.current && !widget.current) {
        widget.current = window.turnstile.render(box.current, {
          sitekey: siteKey, action, callback: setToken, 'expired-callback': () => setToken(null), 'error-callback': () => setBlocked(true),
        });
      }
    };
    if (window.turnstile) return render();
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = render;
    s.onerror = () => setBlocked(true); // e.g. an ad blocker: point to email instead of a dead button
    document.head.appendChild(s);
  }, [siteKey]);

  /** After a refused submit: a token works once, so the widget starts again. */
  const reset = () => {
    if (widget.current) window.turnstile?.reset(widget.current);
    setToken(null);
  };
  return { siteKey, token, blocked, box, reset };
}
